/**
 * Expert mode (2026-09-29): the one preference that decides how much of the Forecast tool a
 * reader sees. Off by default.
 *
 * Off, the primary navigation is the five destinations a planner needs on an ordinary day — the
 * Dashboard, the Heatmap, Assignments (joined 2026-10-05), Project Intelligence and Data
 * Hygiene — and the filter bar keeps the
 * window, the grain, the dimension, the opportunity mode and the comparison and drops the seven
 * narrowing filters. On, everything is there. The Estimate tool is not touched either way: its
 * three destinations are not Forecast views.
 *
 * It is a view preference, so it lives beside the theme in localStorage rather than on the
 * server. A link is still the state (PRD 7.2): a filter a link carries is applied whether or not
 * the bar shows the control for it, so a pasted address lands on the same numbers for everybody,
 * and "Clear filters" still clears what a reader cannot see.
 *
 * Pure, and shared with the client through public/js/lib/expert.js.
 */

/** The localStorage key. Present and `'on'` means on; anything else is off. */
export const EXPERT_KEY = 'waag.expert';

/** The Forecast destinations everybody sees. */
export const BASIC_NAV_PATHS = Object.freeze([
  '/',
  '/heatmap',
  '/assignments',
  '/projects',
  '/hygiene',
]);

/**
 * The navigation items to draw. Forecast's list is cut to the basic five unless expert mode is
 * on; any other tool's list is returned as it is.
 * @template {{ path: string, tool?: string }} T
 * @param {readonly T[]} items
 * @param {boolean} expert
 * @returns {T[]}
 */
export function visibleNav(items, expert) {
  return items.filter(
    (item) => expert || item.tool !== 'forecast' || BASIC_NAV_PATHS.includes(item.path),
  );
}

/** The filter-bar controls only an expert sees, by their filter-state key. */
export const EXPERT_FILTER_KEYS = Object.freeze([
  'types',
  'services',
  'departments',
  'people',
  'statuses',
  'tiers',
]);

/**
 * Dimensions the bar no longer offers anybody. Office was withdrawn on 2026-09-29: the company
 * has one office, so the dimension split nothing and the filter narrowed nothing. The API and
 * the views still understand `dim=office`, so an old link is not broken; the bar just does not
 * propose it.
 */
export const WITHDRAWN_DIMENSIONS = Object.freeze(['office']);

/**
 * The dimensions the "By" control offers: the shared list without the withdrawn ones, plus the
 * one the URL already carries when it is withdrawn, so a pasted `dim=office` link still says so.
 * @param {readonly string[]} dimensions  the shared DIMENSIONS list
 * @param {string | null | undefined} current
 */
export function barDimensions(dimensions, current) {
  return dimensions.filter((d) => !WITHDRAWN_DIMENSIONS.includes(d) || d === current);
}

/** Whether the bar draws the control for a filter key. */
export function showsFilter(key, expert) {
  return Boolean(expert) || !EXPERT_FILTER_KEYS.includes(key);
}

/** The heatmap renderings everybody sees, by key: the matrix and the small multiples. */
export const BASIC_HEATMAP_RENDERINGS = Object.freeze(['matrix', 'multiples']);

/**
 * The heatmap renderings to offer. The horizon, the beeswarm, the mosaic and the rank chart are
 * readings for somebody who already knows the matrix; a non-expert gets the two that need no
 * introduction.
 * @template {{ key: string }} T
 * @param {readonly T[]} renderings
 * @param {boolean} expert
 * @returns {T[]}
 */
export function visibleRenderings(renderings, expert) {
  return renderings.filter((r) => expert || BASIC_HEATMAP_RENDERINGS.includes(r.key));
}

/**
 * The cards only an expert sees, by element name (2026-10-08): the capacity bridge under the
 * Dashboard's ribbon, the contention map under the Assignments matrix, and the quality metrics
 * and the source flow under the Data Hygiene worklist. Each is the page's first card taken apart
 * for somebody who already reads it; the first card is the page for everybody else. A card that
 * is not shown is not mounted, so it does not fetch.
 */
export const EXPERT_CARDS = Object.freeze([
  'capacity-bridge',
  'contention-map',
  'quality-metrics',
  'source-flow',
]);

/** Whether a page mounts a card, by its element name. */
export function showsCard(tag, expert) {
  return Boolean(expert) || !EXPERT_CARDS.includes(tag);
}

/**
 * The window lengths the bar offers. 52 weeks was withdrawn on 2026-09-29: a year is not a
 * planning window anybody staffs against, and the wide view made every chart illegible.
 */
export const WEEK_CHOICES = Object.freeze([4, 8, 13, 26]);

/**
 * The options for the window control: the choices, plus the window the URL already carries when
 * it is not one of them, so a pasted link with `weeks=52` shows 52 rather than silently reading
 * as 4. In ascending order either way.
 * @param {number} current
 * @returns {number[]}
 */
export function weekChoices(current) {
  const out = new Set(WEEK_CHOICES);
  if (Number.isInteger(current) && current > 0) out.add(current);
  return [...out].sort((a, b) => a - b);
}
